import React from 'react';
import { InputTextArea } from '@spark';
import ToolSourceCard from './ToolSourceCard';
import ToolExecutionFields, { MANUAL_EXECUTION_LABEL } from './ToolExecutionFields';
import ToolsFoundList from './ToolsFoundList';
import { MANUAL_TOOLS_PLACEHOLDER, parseManualTools } from './toolSourceParsing';

/**
 * "Manual Tool Discovery" card (Figma 4012:16639 / 4012:16848): pasted JSON
 * tool definitions parsed client-side into "Tools Selected", beside the single
 * "Tool Execution (MCP/Rest API) *" URL field.
 */
const ManualToolSourceCard = ({ source, errors = {}, onChange, onRemove }) => {
    const parse = parseManualTools(source.tools_json);
    const parseError = source.tools_json?.trim() ? parse.error : null;

    const handleToolsJsonChange = (text) => {
        const { tools } = parseManualTools(text);
        const previousNames = new Set(source.tools.map((t) => t.name));
        const previouslySelected = new Set(source.selected_tools);
        onChange({
            tools_json: text,
            tools,
            // A tool that already existed keeps its checked state; tools that
            // appear with this edit start checked.
            selected_tools: tools
                .map((t) => t.name)
                .filter((name) => !previousNames.has(name) || previouslySelected.has(name)),
        });
    };

    const toggleTool = (name) => {
        const selected = new Set(source.selected_tools);
        if (selected.has(name)) selected.delete(name);
        else selected.add(name);
        onChange({ selected_tools: source.tools.map((t) => t.name).filter((n) => selected.has(n)) });
    };

    /** Bulk "Select All" / "Clear All": keeps `selected_tools` in `tools` order. */
    const setSelectedTools = (names) => {
        const selected = new Set(names);
        onChange({ selected_tools: source.tools.map((t) => t.name).filter((n) => selected.has(n)) });
    };

    return (
        <ToolSourceCard type={source.type} onRemove={onRemove}>
            <div className="cw-ac-manual__row">
                <div className="cw-ac-manual__editor">
                    <InputTextArea
                        showLabel={false}
                        showHelperText={false}
                        stretch
                        value={source.tools_json}
                        placeholder={MANUAL_TOOLS_PLACEHOLDER}
                        aria-label="Tool definitions (JSON)"
                        spellCheck={false}
                        onChange={(e) => handleToolsJsonChange(e.target.value)}
                        className="cw-ac-manual__textarea"
                    />
                    {(parseError || errors.tools) && (
                        <div className="cw-field-error">{parseError || errors.tools}</div>
                    )}
                </div>
                <ToolExecutionFields
                    label={MANUAL_EXECUTION_LABEL}
                    url={source.execution_url}
                    onUrlChange={(next) => onChange({ execution_url: next })}
                    error={errors.execution_url}
                />
            </div>
            <ToolsFoundList
                tools={source.tools}
                selectedTools={source.selected_tools}
                onToggle={toggleTool}
                onSelectedChange={setSelectedTools}
            />
        </ToolSourceCard>
    );
};

export default ManualToolSourceCard;
